import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const STORAGE_KEY = "inspectorPortalLanguage";

export type SupportedLanguage = "en" | "de";

type LanguageContextValue = {
  language: SupportedLanguage;
  setLanguage: (language: string) => void;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

function resolveLanguage(language: string | null): SupportedLanguage {
  return language === "de" ? "de" : "en";
}

function getInitialLanguage(): SupportedLanguage {
  if (typeof localStorage === "undefined") {
    return "en";
  }

  try {
    return resolveLanguage(localStorage.getItem(STORAGE_KEY));
  } catch {
    return "en";
  }
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] =
    useState<SupportedLanguage>(getInitialLanguage);
  const languageRef = useRef(language);
  const expectedDocumentLanguageRef = useRef(language);

  useEffect(() => {
    languageRef.current = language;
    expectedDocumentLanguageRef.current = language;
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type !== "attributes" ||
          mutation.attributeName !== "lang"
        ) {
          continue;
        }

        const expectedLanguage = expectedDocumentLanguageRef.current;

        if (document.documentElement.lang === expectedLanguage) {
          continue;
        }

        expectedDocumentLanguageRef.current = languageRef.current;
        document.documentElement.lang = languageRef.current;
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang"],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const setLanguage = useCallback((nextLanguage: string) => {
    const supported = resolveLanguage(nextLanguage);
    languageRef.current = supported;
    expectedDocumentLanguageRef.current = supported;
    setLanguageState(supported);

    try {
      localStorage.setItem(STORAGE_KEY, supported);
    } catch {
      // Keep the in-memory selection even when browser storage is blocked.
    }

    document.documentElement.lang = supported;
  }, []);

  const value = useMemo(
    () => ({ language, setLanguage }),
    [language, setLanguage],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }

  return context;
}
